import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { FC, memo, useCallback, useEffect, useState } from 'react'
import { useToast } from 'hooks/useToast'
import { useTranslation } from '@crew/modules/i18n'
import { useModal } from 'components/layouts/modal/useModal'
import { ContextMenuItems } from 'enums/app'
import { useMessageUrl } from 'hooks/useMessageUrl'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { CrewDropDownButton } from 'components/elements/crewDropDownButton/crewDropDownButton'
import MoreHoriz from '~icons/material-symbols/more-horiz'
import { useAiAssistantMessageActionMenu } from './useAiAssistantMessageActionMenu'

// Dropdown item render
const renderDropDownItem = (itemData: { name: string }) => (
  <span className="px-1.5 text-base whitespace-nowrap">{itemData.name}</span>
)

export type AiAssistantMessageActionMenuProps = {
  visible: boolean
  chatMessageId: string
  chatMessageVersion: number
  onEditButtonClick?: () => void
  enabledActionMenus: ContextMenuItems[] // 表示するアクションメニューのリスト
  onClickChatReadStatus?: (chatMessageId: string) => void
}

export const AiAssistantMessageActionMenu: FC<AiAssistantMessageActionMenuProps> =
  memo((props: AiAssistantMessageActionMenuProps) => {
    const { contextMenuData, deleteChatMessage, isLoadingDeleteChatMessage } =
      useAiAssistantMessageActionMenu(props.enabledActionMenus)

    const { t } = useTranslation()
    const { success } = useToast()
    const [showApiErrors] = useShowApiErrors()
    const messageUrl = useMessageUrl(props.chatMessageId)

    const [visibleActionMenu, setVisibleActionMenu] = useState<boolean>(false)
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
      useModal()

    // TODO: コンテキストメニューを表示した状態での、ホバーメニューの表示・非表示に問題あり。
    // https://break-tmc.atlassian.net/browse/CREW-4027

    // メニューの表示・非表示切り替え
    useEffect(() => {
      if (!showContextMenu) {
        // コンテキストメニューが非表示の時のみ、切り替えを受け付ける
        setVisibleActionMenu(props.visible)
      }
      // contextMenuの外側をクリックした際にprops.visibleを受け取りたいので全て依存に入れる
    }, [props.visible, showContextMenu])

    // ドロップダウンの「編集」押下時の
    const handleEditButtonClick = useCallback(() => {
      props.onEditButtonClick?.()
    }, [props])

    // ContextMenu内のitemクリック時のイベントハンドラ
    const handleClickItem = useCallback(
      (itemData: { id: string }) => {
        if (itemData.id === ContextMenuItems.Edit.action) {
          handleEditButtonClick()
        } else if (itemData.id === ContextMenuItems.Delete.action) {
          setConfirmMessage(t('message.general.confirmMessage.delete'))
          openConfirmDialog()
        } else if (itemData.id === ContextMenuItems.CopyLink.action) {
          navigator.clipboard.writeText(messageUrl)
          success(t('message.file.copySuccess'))
        } else if (itemData.id === ContextMenuItems.ReadStatus.action) {
          props.onClickChatReadStatus?.(props.chatMessageId)
        }
      },
      [handleEditButtonClick, messageUrl, openConfirmDialog, props, success, t]
    )

    // ContextMenuが表示された時のイベントハンドラ
    const handleShowingItem = useCallback(() => {
      setShowContextMenu(true)
    }, [])

    // ContextMenuが非表示になった時のイベントハンドラ
    const handleHidingItem = useCallback(() => {
      setShowContextMenu(false)
    }, [])

    // 削除確認ダイアログの許可ボタンクリック時のイベントハンドラ
    const handleDeletePermitButtonClick = useCallback(async () => {
      try {
        await deleteChatMessage(props.chatMessageId, props.chatMessageVersion)
        // チャットメッセージ削除成功時に画面上のItemを外す
        // websocket経由で削除する
        success(t('message.chat.messageDeleted'))
      } catch (err) {
        console.error(
          '[useCrewChatMessageActionMenu] chat message delete error.',
          JSON.stringify(err)
        )
        showApiErrors(err)
      }
      closeConfirmDialog()
    }, [
      closeConfirmDialog,
      deleteChatMessage,
      props.chatMessageId,
      props.chatMessageVersion,
      showApiErrors,
      success,
      t,
    ])

    return (
      <>
        {visibleActionMenu && (
          // メニュー表示時
          <div className="crew-bg-default flex flex-row items-center border gap-x-1">
            {/* 編集・削除 */}
            <CrewDropDownButton
              icon={<MoreHoriz width={24} height={24} />}
              items={contextMenuData}
              keyExpr="id"
              displayExpr="name"
              onItemClick={handleClickItem}
              showArrowIcon={false}
              showDivider={false}
              highlightSelected={false}
              stylingMode="text"
              buttonSize="sm"
              itemRender={renderDropDownItem}
              onShowing={handleShowingItem}
              onHiding={handleHidingItem}
            />
          </div>
        )}
        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
          permitButtonDisabled={isLoadingDeleteChatMessage}
        />
      </>
    )
  })
